import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
// icons
import { AiOutlinePlus } from 'react-icons/ai';
import { formatCurrency } from 'utils/currencyFormatter';
// contants
import {
  getOrderStatus,
  getPaymentMethod,
  getPaymentStatus
} from '../../../constants';
//helpers
import { verificateNullValue } from 'helpers';
// components
import TextArea from 'components/backoffice/customInput/textAreaCustom';
import SearchLibraryInput from 'components/backoffice/customInput/SearchLibraryInput';
import SearchCompanyInput from 'components/backoffice/customInput/SearchCompanyInput';
import SearchUserInput from 'components/backoffice/customInput/SearchUserInput';
import Select from 'components/backoffice/selects/SelectCustom';
import Subtitle from 'components/backoffice/texts/subtitle';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import Button from 'components/backoffice/buttonCustom';
import Input from 'components/backoffice/customInput';
import ArticlesHeader from 'components/backoffice/headers/articles';
import ProductOrder from './productOrder';
import SaveIcon from 'components/backoffice/icons/SaveIcon';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const OrderForm = ({ onSubmit, data = {}, isEditing, loading }) => {
  const { t } = useTranslation(['backoffice', 'contact', 'shipping']);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedFields = watch();

  useEffect(() => {
    if (Object.keys(data).length > 0) {
      setValue('usuario', verificateNullValue(data.usuario))
      setValue('biblioteca', verificateNullValue(data.biblioteca))
      setValue('benefited_company', verificateNullValue(data.benefited_company))
      setValue('descuento', data.descuento)
      setValue('comentario', data.comentario)
      setValue('reembolso', data.reembolso)
      setValue('order_status', data.order_status)
      setValue('payment_method', data.payment_method)
      setValue('payment_status', data.payment_status)
      setValue('commission_amount', data.commission_amount)
      setValue('commission_for_instore_pickup', data.commission_for_instore_pickup)
      setValue('commission_status_display', data.commission_status_display)
      setValue('shipping_commission', data.shipping_commission)
      data?.articles?.forEach((item) => {
        append({
          product: item.product,
          discount: item.descuento,
          editorial: item.product.editorial,
          price: parseFloat(item.precio_venta).toFixed(2),
          total_price: item.total,
          quantity: item.quantity,
          state: item.estado
        });
      });
    }
  }, [data]);

  const orderStatus = getOrderStatus(t);

  const paymentMethod = getPaymentMethod(t);

  const paymentStatus = getPaymentStatus(t);

  const onAddProduct = () => {
    append({ product: null, discount: 0, editorial: '', price: 0, total_price: 0, quantity: 1, state: 0 });
  };

  return (
    <div className='mt-5'>
      {isEditing && (
        <Row className='border-grey p-2 mb-3'>
          <Col md={4} className="d-flex flex-column align-items-start justify-content-center my-2">
            <p className="text-light m-0">{t('product.quantity')}: {formatCurrency(Number(data.commission_amount) || 0)}</p>
          </Col>
          <Col md={4} className="d-flex flex-column align-items-start justify-content-center my-2">
            <p className="text-light m-0">{t('instorePickup')}: {data.commission_for_instore_pickup || '0'}%</p>
          </Col>
          {data.shipping_commission !== 0 && (
            <Col md={4} className="d-flex flex-column align-items-start justify-content-center my-2">
              <p className="text-light m-0">
                {t('shippingCommission')}: {formatCurrency(data.shipping_commission || 0)}
              </p>
            </Col>
          )}
          <Col md={4} className="d-flex flex-column align-items-start justify-content-center my-2">
            <p className="text-light m-0">
              {t('state')}: {data.commission_status_display || t('notDefined')}
            </p>
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="usuario"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) =>
              <div className='w-100 orders' data-testid="autocomplete_user">
                <SearchUserInput
                  onSelected={(item) => onChange(item)}
                  defaultUser={value}
                />
              </div>
            }
          />
          {errors.usuario && <small className="text-danger ">{errors.usuario.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="biblioteca"
            control={control}
            render={({ field: { onChange, value } }) =>
              <div className='w-100 orders' data-testid="autocomplete_library">
                <SearchLibraryInput
                  onSelected={(item) => onChange(item)}
                  defaultLibrary={value}
                />
              </div>
            }
          />
          {errors.biblioteca && <small className="text-danger ">{errors.biblioteca.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="benefited_company"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) =>
              <div className='w-100 orders' data-testid="autocomplete_company">
                <SearchCompanyInput
                  onSelected={(item) => onChange(item)}
                  defaultCompany={value}
                />
              </div>
            }
          />
          {errors.benefited_company && <small className="text-danger ">{errors.benefited_company.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="order_status"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.orderStatus')}
                placeholder={t('orderTrans.selectOrderStatus')}
                choices={orderStatus}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.order_status && <small className="text-danger ">{errors.order_status.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="payment_method"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.paymentMethod')}
                placeholder={t('orderTrans.selectPaymentMethod')}
                choices={paymentMethod}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.payment_method && <small className="text-danger ">{errors.payment_method.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="payment_status"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                label={t('orderTrans.paymentStatus')}
                placeholder={t('orderTrans.selectPaymentStatus')}
                choices={paymentStatus}
                value={value}
                onCheckedItem={onChange}
              />
            )}
          />
          {errors.payment_status && <small className="text-danger ">{errors.payment_status.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="comentario"
            control={control}
            render={({ field }) =>
              <TextArea label={t('comment')} placeholder={t('comment')} {...field} />
            }
          />
          {errors.comentario && <small className="text-danger ">{errors.comentario.message}</small>}
        </Col>
        {isEditing && data?.contact_info && Object.keys(data?.contact_info).length > 0 && <Col sm={12} md={12} lg={4} className="mb-4 d-flex flex-column">
          <Form.Label className="text-light">{t('contact', { ns: 'contact' })}</Form.Label>
          <div className="d-flex flex-column border-grey p-3 h-100">
            <span className="text-light">
              <span className='fw-bold'>{`${t('name')}: `}</span>{data?.contact_info?.name || ''}
            </span>
            <span className="text-light mt-3 fw-bold">{`${t('emailOrPhone', { ns: 'shipping' })}:`}</span>
            <span className="text-light">{data?.contact_info?.email_phone || ''}</span>
          </div>
        </Col>}
        <Col sm={12} md={12} lg={4} className="mb-4">
          <Controller
            name="descuento"
            control={control}
            defaultValue="0"
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <div className='w-100'>
                <Input
                  type={'number'}
                  label={t('product.discount')}
                  placeholder='%'
                  value={value}
                  onChange={onChange}
                />
              </div>
            )}
          />
          {errors.descuento && <small className="text-danger ">{errors.descuento.message}</small>}
        </Col>
        <Col sm={12} md={12} lg={4} className="mb-4 align-self-start">
          <Controller
            name="reembolso"
            control={control}
            render={({ field }) => (
              <CustomCheckbox
                label={t('orderTrans.refund')}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              /*  className='h-auto p-0' */
              />
            )}
          />
          {errors.reembolso && <small className="text-danger ">{errors.reembolso.message}</small>}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Subtitle text={t('products')} />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-end pb-4">
          <Button color="terciary" onClick={onAddProduct} type={'button'}>
            {t('product.addProduct')} <AiOutlinePlus />
          </Button>
        </Col>
        <ArticlesHeader />
      </Row>
      <div data-testid="productOrderForm">
        {fields.map((field, index) => (
          <ProductOrder
            key={index}
            id={index}
            article={field}
            onDelete={() => remove(index)}
            control={control}
            setValue={setValue}
            watchedFields={watchedFields}
          />
        ))}
      </div>
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button
          color="terciary"
          onClick={() => navigate(-1)}
          type={'button'}
        >
          {t('cancel')}
        </Button>

        <Button
          color="primary"
          onClick={handleSubmit(onSubmit)}
          type={'submit'}
          data-testid="save"
        >
          {t('save')}
          {loading ? <SpinnerComponent loading={loading} /> : <SaveIcon />}
        </Button>
      </div>
    </div>
  );
};

OrderForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired
};

export default OrderForm;
